<script setup lang="ts">
import { Icon } from '@iconify/vue';
/**
 * UiBreadcrumb - Design System Breadcrumb Component
 *
 * Displays navigation hierarchy to help users understand their location.
 *
 * @example
 * <UiBreadcrumb
 *   :items="[
 *     { label: 'Home', to: '/' },
 *     { label: 'Projects', to: '/projects' },
 *     { label: 'Current Project' },
 *   ]"
 * />
 */
import { computed } from 'vue';
import { RouterLink } from 'vue-router';

export interface BreadcrumbItem {
   /** Display text */
   label: string;
   /** Route path - if omitted, renders as current page (no link) */
   to?: string;
}

export interface UiBreadcrumbProps {
   /** Array of breadcrumb items */
   items: BreadcrumbItem[];
   /** Separator style */
   separator?: 'arrow' | 'slash' | 'chevron';
}

const props = withDefaults(defineProps<UiBreadcrumbProps>(), {
   separator: 'arrow',
});

const separatorIcon = computed(() => {
   const icons = {
      arrow: 'ph:arrow-right',
      slash: 'ph:slash-forward',
      chevron: 'ph:caret-right',
   };
   return icons[props.separator];
});
</script>

<template>
   <nav aria-label="Breadcrumb">
      <ol class="flex items-center gap-2 text-sm text-gray-500">
         <template v-for="(item, index) in items" :key="index">
            <!-- Separator (not before first item) -->
            <li v-if="index > 0" aria-hidden="true">
               <Icon :icon="separatorIcon" class="size-3" />
            </li>

            <!-- Breadcrumb item -->
            <li>
               <RouterLink
                  v-if="item.to"
                  :to="item.to"
                  class="hover:text-primary transition-colors"
               >
                  {{ item.label }}
               </RouterLink>
               <span v-else class="text-black font-medium">
                  {{ item.label }}
               </span>
            </li>
         </template>
      </ol>
   </nav>
</template>
